import {orderBy, slice} from 'lodash';
import {DataProviderDataResponse} from '@/services/datatable/datatable.service';
import {SearchProvider} from '@/services/datatable/abstract-datatable-search-provider.service';
import {DatatableSearchExpressionService} from '@/services/datatable/datatable-search-expressions.service';


/**
 * LocalSearchProvider is a copy of the API.
 * Needs to fetch all Data and provides the same functionality as the lazy one.
 */
class LocalSearchProvider extends SearchProvider {

    getData(inputConfig: DisguiseDto.DTInputConfigurationDto): Promise<DataProviderDataResponse> {
        return new Promise<DataProviderDataResponse>(((resolve) => {
            let result = this.applyPagingAndReturnSearchInfo(
                this.applySortOrder(
                    this.applyFilters(this.initialTableData, inputConfig),
                    inputConfig
                ),
                inputConfig
            );


            if (result.page > result.totalPages || inputConfig.forceLastPage) {
                let newInputConfig;

                // load last page if requested
                if (inputConfig.forceLastPage === true) {
                    newInputConfig = {...inputConfig, page: result.totalPages - 1};
                } else {
                    newInputConfig = {...inputConfig, page: 0};
                }
                result = this.applyPagingAndReturnSearchInfo(
                    this.applySortOrder(
                        this.applyFilters(this.initialTableData, newInputConfig),
                        newInputConfig
                    ),
                    newInputConfig
                );
            }
            resolve(result);
        }));
    }

    getInitialData(inputConfig: DisguiseDto.DTInputConfigurationDto): DataProviderDataResponse {
        return this.applyPagingAndReturnSearchInfo(this.applyFilters(this.initialTableData, inputConfig), inputConfig);
    }

    private applyPagingAndReturnSearchInfo(table: DisguiseDto.DTTemplateDto,
                                           inputConfig: DisguiseDto.DTInputConfigurationDto):
        DataProviderDataResponse {

        const totalPages = Math.ceil(((table.rows || []).length || 1) / (inputConfig.size || 10));
        const pageSize = inputConfig.size || 10;
        let startIndex = 0;
        if (inputConfig.page && inputConfig.size) {
            startIndex = inputConfig.page * pageSize;
        }

        table = this.applyPagingFilter(table, startIndex, pageSize);

        return {
            data: table,
            page: inputConfig.page || 0,
            size: pageSize,
            maxSize: 100,
            totalPages
        };
    }

    private applyPagingFilter(table: DisguiseDto.DTTemplateDto, startIndex: number, pageSize: number)
        : DisguiseDto.DTTemplateDto {
        table.rows = slice(table.rows, startIndex, startIndex + pageSize);
        return table;
    }

    private applySortOrder(table: DisguiseDto.DTTemplateDto,
                           inputConfig: DisguiseDto.DTInputConfigurationDto)
        : DisguiseDto.DTTemplateDto {
        if (table.rows) {
            const sortColumnConfigs = [];
            // 1. extract sort column config
            for (const columnConfig of inputConfig?.columns || []) {
                if (columnConfig.sort) {
                    sortColumnConfigs.push(columnConfig);
                }
            }
            // 2. order them by sort rank
            sortColumnConfigs.sort((a, b) => {
                let result = 0;
                if (a.sortRank && b.sortRank) {
                    result = b.sortRank - a.sortRank;
                }
                return result;
            });

            // the function, that returns the value for the current row
            const iteratees = [];
            // array matching index of iteratees, containing 'asc' or 'desc'
            const orders: Array<boolean | 'asc' | 'desc'> = [];

            for (const sortColumn of sortColumnConfigs) {
                const columnIndex = sortColumn.index || 0;

                iteratees.push((row: DisguiseDto.DTRowDto) => {
                    let value: number | string = (row.cells || [])[columnIndex].value || '';
                    if ((table.columns || [])[sortColumn.index || 0].dataType === 'NUMBER') {
                        value = Number(value);
                    }
                    return value;
                });
                orders.push(sortColumn.sort === 'ASC' ? 'asc' : 'desc');
            }
            table.rows = orderBy(table.rows, iteratees, orders);
        }

        return table;
    }

    /* Case-insensitive search. True if the element is inside search. */
    private matchSearch(element: string,
                        search: string,
                        numeric: boolean = false,
                        mustStartWith: boolean = false
    ): boolean {
        if (mustStartWith) {
            return element.indexOf(search) === 0;
        } else {
            if (numeric) {
                return Number(element) === Number(search);
            }
            return (
                element.toLowerCase().indexOf(search.toLowerCase()) > -1
            );
        }
    }

    private applyFilters(table: DisguiseDto.DTTemplateDto,
                         inputConfig: DisguiseDto.DTInputConfigurationDto):
        DisguiseDto.DTTemplateDto {
        let result: DisguiseDto.DTTemplateDto = {...table, rows: []};
        if (table.rows) {
            for (const currentRow of table.rows) {
                let found = this.fulltextSearchFilter(currentRow, inputConfig);
                found = found && this.columnTextSearchFilter(currentRow, inputConfig, table);
                found = found && this.columnAdvancedSearchFilter(currentRow, inputConfig, table);
                if (found) {
                    result.rows?.push(currentRow);
                }
            }
            result = this.filterStringRanges(inputConfig, result);
        }
        return result;
    }

    private filterStringRanges(inputConfig: DisguiseDto.DTInputConfigurationDto,
                               table: DisguiseDto.DTTemplateDto): DisguiseDto.DTTemplateDto {
        const result = table;
        // this is now the most complex part -> does a range search over strings
        for (const inputColumnConfiguration of inputConfig.columns || []) {
            for (const searchFilter of inputColumnConfiguration.searchValues || []) {
                const dataType = (table.columns || [])[inputColumnConfiguration.index || 0].dataType || 'TEXT';
                if (DatatableSearchExpressionService.isStringRangeExpression(searchFilter, dataType)) {
                    const cellIndex = inputColumnConfiguration.index || 0;

                    // build a mapping (cell -> row). similar like a hash table,
                    // but hash table can hold primitive types as a key only

                    // if cell (key) matches -> we will add the row (value)
                    const cellValues: Array<{
                        cell: DisguiseDto.DTCellDto,
                        row: DisguiseDto.DTRowDto
                    }> = [];
                    for (const row of table.rows || []) {
                        cellValues.push({cell: (row.cells || [])[cellIndex], row});
                    }

                    // phase 1: sort all cells
                    cellValues.sort(((a, b) => {
                        const aValue: string = a.cell.value?.toLowerCase() || '';
                        const bValue: string = b.cell.value?.toLowerCase() || '';
                        if (aValue && bValue) {
                            if (aValue < bValue) {
                                return -1;
                            }
                            if (bValue < aValue) {
                                return 1;
                            }
                        }
                        return 0;
                    }));

                    // phase 2: find start & end in the sorted array
                    let startIndex = 0;
                    let endIndex = cellValues.length - 1;
                    const minSearchValueModified = (searchFilter.min || '').trim().toLowerCase();
                    const maxSearchValueModified = (searchFilter.max || '').trim().toLowerCase();

                    if (minSearchValueModified) {
                        // forward
                        for (let forwardCellIteratorIndex = 0;
                             forwardCellIteratorIndex < cellValues.length;
                             forwardCellIteratorIndex++) {
                            const cellValue = cellValues[forwardCellIteratorIndex];
                            const cellValueModified = (cellValue.cell.value || '').trim().toLowerCase()
                                .substr(0, minSearchValueModified.length);
                            if (cellValueModified && cellValueModified >= minSearchValueModified) {
                                startIndex = forwardCellIteratorIndex;
                                break;
                            }
                        }
                    }
                    if (maxSearchValueModified) {
                        // backward
                        for (let backwardCellIteratorIndex = cellValues.length - 1;
                             backwardCellIteratorIndex >= 0;
                             backwardCellIteratorIndex--) {
                            const cellValue = cellValues[backwardCellIteratorIndex];
                            const cellValueModified = (cellValue.cell.value || '').trim().toLowerCase()
                                .substr(0, maxSearchValueModified.length);
                            if (cellValueModified && maxSearchValueModified >= cellValueModified) {
                                endIndex = backwardCellIteratorIndex;
                                break;
                            }
                        }
                    }

                    // phase 3:
                    // rebuild the result based on the reduced reorga array
                    const newResult: DisguiseDto.DTRowDto[] = [];
                    if (!searchFilter.negate) {
                        for (let cellIteratorIndex = startIndex;
                             cellIteratorIndex <= endIndex;
                             cellIteratorIndex++) {
                            newResult.push((cellValues || [])[cellIteratorIndex].row);
                        }
                    } else {
                        for (let cellIteratorIndex = 0;
                             cellIteratorIndex < startIndex;
                             cellIteratorIndex++) {
                            newResult.push((cellValues || [])[cellIteratorIndex].row);
                        }
                        for (let cellIteratorIndex = endIndex + 1;
                             cellIteratorIndex < cellValues.length;
                             cellIteratorIndex++) {
                            newResult.push((cellValues || [])[cellIteratorIndex].row);
                        }
                    }
                    result.rows = newResult;
                }
            }
        }
        return result;
    }

    /* Returns true if the full text search string is empty or matches a value inside a row. */
    private fulltextSearchFilter(row: DisguiseDto.DTRowDto,
                                 inputConfig: DisguiseDto.DTInputConfigurationDto): boolean {
        let found = false;
        const fullTextSearchValue = inputConfig.searchValue;
        if (!fullTextSearchValue) {
            found = true;
        } else if (row.cells) {
            for (const currentCell of row.cells) {
                if (currentCell.value) {
                    for (const fullText of fullTextSearchValue.split(' ')) {
                        if (this.matchSearch(currentCell.value, fullText)) {
                            found = true;
                            break;
                        }
                    }
                }
            }
        }
        return found;
    }

    private columnTextSearchFilter(row: DisguiseDto.DTRowDto,
                                   inputConfig: DisguiseDto.DTInputConfigurationDto,
                                   table: DisguiseDto.DTTemplateDto): boolean {
        let found: boolean;
        if (row.cells && inputConfig.columns) {
            found = true;
            for (let cellIndex = 0; cellIndex < row.cells.length; cellIndex++) {
                const configuredColumns = inputConfig.columns;
                const columnSearchValue = configuredColumns[cellIndex].searchValue || '';
                if (!columnSearchValue) {
                    // has no search value
                    continue;
                }

                const currentCellValue = row.cells[cellIndex].value;
                found = found && this.matchSearch(
                    currentCellValue || '',
                    columnSearchValue,
                    (table.columns || [])[cellIndex].dataType === 'NUMBER',
                    true
                );
            }
        } else {
            found = true;
        }
        return found;
    }

    private findByOrCriteria(searchCriteria: DisguiseDto.SearchValues[],
                             currentCellValue: string,
                             currentCellDataType: DisguiseDto.EnumDataType) {
        let rowMatchesAnyFilterCriteria = searchCriteria.length === 0;
        for (const currentSearchValue of searchCriteria) {
            if (DatatableSearchExpressionService.isStringRangeExpression(currentSearchValue, currentCellDataType)) {
                rowMatchesAnyFilterCriteria = true;
            } else {
                if (currentSearchValue.equal) {
                    if (currentCellDataType === 'NUMBER') {
                        rowMatchesAnyFilterCriteria = DatatableSearchExpressionService.numberExpressionMatches(
                          currentSearchValue,
                          Number(currentCellValue)
                        );
                    } else {
                        rowMatchesAnyFilterCriteria = DatatableSearchExpressionService.stringLikeExpressionMatches(
                          currentSearchValue,
                          currentCellValue
                        );
                    }
                } else {
                    const currentCellValueNumber = Number(currentCellValue);
                    rowMatchesAnyFilterCriteria =
                        DatatableSearchExpressionService.numberRangeExpressionMatches(
                            currentSearchValue,
                            currentCellValueNumber
                        );
                }
            }
            if (rowMatchesAnyFilterCriteria) {
                break;
            }
        }
        return rowMatchesAnyFilterCriteria;
    }

    private findByAndCriteria(searchCriteria: DisguiseDto.SearchValues[],
                              currentCellValue: string,
                              currentCellDataType: DisguiseDto.EnumDataType) {
        let rowMatchesAnyFilterCriteria = true;
        const currentCellValueNumber = Number(currentCellValue);
        for (const currentSearchValue of searchCriteria) {
            if (!DatatableSearchExpressionService.isStringRangeExpression(currentSearchValue, currentCellDataType)) {
                if (currentSearchValue.equal) {
                    let conditionMatches;
                    if (!isNaN(currentCellValueNumber)) {
                        conditionMatches = DatatableSearchExpressionService.numberExpressionMatches(
                            currentSearchValue,
                            currentCellValueNumber
                        );
                    } else {
                        conditionMatches = DatatableSearchExpressionService.stringLikeExpressionMatches(
                            currentSearchValue,
                            currentCellValue
                        );
                    }
                    rowMatchesAnyFilterCriteria = rowMatchesAnyFilterCriteria && conditionMatches;
                } else {
                    rowMatchesAnyFilterCriteria = rowMatchesAnyFilterCriteria
                        && DatatableSearchExpressionService.numberRangeExpressionMatches(
                            currentSearchValue,
                            Number(currentCellValue)
                        );
                }
            }
        }
        return rowMatchesAnyFilterCriteria;
    }


    private columnAdvancedSearchFilter(row: DisguiseDto.DTRowDto,
                                       inputConfig: DisguiseDto.DTInputConfigurationDto,
                                       table: DisguiseDto.DTTemplateDto) {
        let rowMatchesSearchCriteria: boolean = true;
        if (row.cells && inputConfig.columns) {
            for (let cellIndex = 0; cellIndex < row.cells.length; cellIndex++) {
                const configuredColumns = inputConfig.columns;
                const columnConfig = configuredColumns[cellIndex];
                const columnSearchValues = columnConfig.searchValues;
                if (!columnSearchValues || !columnSearchValues.length) {
                    // has no search value
                    continue;
                }
                const positives: DisguiseDto.SearchValues[] = [];
                const negatives: DisguiseDto.SearchValues[] = [];
                for (const currentSearchValue of columnConfig.searchValues || []) {
                    if (currentSearchValue.negate) {
                        negatives.push(currentSearchValue);
                    } else {
                        positives.push(currentSearchValue);
                    }
                }

                const currentCellValue = row.cells[cellIndex].value || '';
                const currentCellDataType = (table.columns || [])[cellIndex].dataType || 'TEXT';
                const anyPositiveMatch =
                    this.findByOrCriteria(positives, currentCellValue, currentCellDataType); // or
                const anyNegativeMatch =
                    this.findByAndCriteria(negatives, currentCellValue, currentCellDataType); // and
                rowMatchesSearchCriteria = anyPositiveMatch && anyNegativeMatch;
                if (!rowMatchesSearchCriteria) {
                    break;
                }
            }
        }
        return rowMatchesSearchCriteria;
    }

}

export {
    LocalSearchProvider
};
